/**
 * Colyseus Schema — the wire format for room state.
 *
 * Deliberately kept separate from the plain domain types in ../geometry/types.ts.
 * These classes are about synchronisation; those interfaces are about the game.
 * Mirroring them by hand is a small, obvious cost, and much cheaper than making
 * every piece of pure logic depend on the netcode library.
 *
 * Schema changes are additive-only for now (see technical plan, open questions):
 * reordering or removing a @type field changes the binary layout and breaks any
 * client that has not reloaded.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ArraySchema, MapSchema, Schema, type } from '@colyseus/schema';
export class JointState extends Schema {
    constructor() {
        super(...arguments);
        this.id = '';
        this.x = 0;
        this.y = 0;
    }
}
__decorate([
    type('string')
], JointState.prototype, "id", void 0);
__decorate([
    type('number')
], JointState.prototype, "x", void 0);
__decorate([
    type('number')
], JointState.prototype, "y", void 0);
export class LineState extends Schema {
    constructor() {
        super(...arguments);
        this.id = '';
        this.a = '';
        this.b = '';
        this.health = 0;
        this.strength = 0;
        this.length = 0;
        this.color = '#ffffff';
    }
}
__decorate([
    type('string')
], LineState.prototype, "id", void 0);
__decorate([
    type('string')
], LineState.prototype, "a", void 0);
__decorate([
    type('string')
], LineState.prototype, "b", void 0);
__decorate([
    type('number')
], LineState.prototype, "health", void 0);
__decorate([
    type('number')
], LineState.prototype, "strength", void 0);
__decorate([
    type('number')
], LineState.prototype, "length", void 0);
__decorate([
    type('string')
], LineState.prototype, "color", void 0);
export class ComponentState extends Schema {
    constructor() {
        super(...arguments);
        this.id = '';
        /** One of ComponentType — sent as a string to keep the enum server-side. */
        this.componentType = '';
        this.x = 0;
        this.y = 0;
        /**
         * Internal thrusters and pilot seats start inactive and switch on only when
         * the compartment around them is breached.
         */
        this.active = false;
    }
}
__decorate([
    type('string')
], ComponentState.prototype, "id", void 0);
__decorate([
    type('string')
], ComponentState.prototype, "componentType", void 0);
__decorate([
    type('number')
], ComponentState.prototype, "x", void 0);
__decorate([
    type('number')
], ComponentState.prototype, "y", void 0);
__decorate([
    type('boolean')
], ComponentState.prototype, "active", void 0);
export class ShipState extends Schema {
    constructor() {
        super(...arguments);
        this.id = '';
        /** Chaostree account uuid for player ships; empty for NPCs. */
        this.ownerUuid = '';
        /**
         * The hull's callsign: its owner's chaostree display id.
         *
         * Not the username. A contact on a scanner is a *ship*, and a ship is known by
         * its callsign — the person flying it is a separate question, answered in the
         * radio log where they speak.
         */
        this.callsign = '';
        this.isNpc = false;
        /*
         * Where it is, in the system's own absolute coordinates.
         *
         * Absolute, not scene-relative: every client shifts the world so its *own*
         * ship sits at the origin, so there is no shared scene space to send. A
         * receiver draws a remote ship at `theirs − myOrigin`. Getting that backwards
         * is silent and puts everybody in the wrong place — see `multiplayer-plan.md`.
         */
        this.x = 0;
        this.y = 0;
        this.z = 0;
        /*
         * Which way it is pointing, as a quaternion.
         *
         * This used to be a single `rotation`, from back when the schema was written
         * for a ship that turned on one axis. Ships pitch, yaw and roll, and a
         * quaternion is what the flight loop already keeps — sending Euler angles
         * would mean converting twice and arguing about order at both ends.
         */
        this.qx = 0;
        this.qy = 0;
        this.qz = 0;
        this.qw = 1;
        /*
         * How fast, so a receiver can carry a ship forward between updates.
         *
         * Poses arrive ten times a second against sixty frames drawn, so something has
         * to fill the gaps. Velocity makes that an extrapolation of what the ship was
         * actually doing rather than a guess from two positions.
         */
        this.vx = 0;
        this.vy = 0;
        this.vz = 0;
        /**
         * Not in the game.
         *
         * True for the fifteen-odd seconds of a warp crossing, during which the player
         * is neither drawn nor interactable — they are between systems, and the room
         * they are still nominally in is not where they are.
         */
        this.hidden = false;
        /**
         * Parked, rather than flying.
         *
         * A docked ship is drawn from `stationId` and `padId` instead of from the
         * position above, and the position is not sent while this is true.
         *
         * ── Why a pad and not a point ──────────────────────────────────────────────
         *
         * Because stations orbit. A ship that broadcast the absolute coordinates it
         * landed at would sit exactly where the station used to be, drifting further
         * from its pad for as long as it stayed docked. Naming the pad instead lets
         * each receiver resolve it against the station in its own scene, which is
         * moving in the same way — and it survives the owner reloading the page, which
         * a remembered landing position would not.
         */
        this.docked = false;
        /**
         * What the ship is mass-locked to, if anything, by its label.
         *
         * When set, the position above is an **offset from that body** rather than an
         * absolute point -- and that is the difference between a steady ship and a
         * jittering one.
         *
         * A mass-locked ship inherits its anchor's motion, so a ship holding station
         * beside an orbiting station is moving at orbital speed in absolute terms.
         * Sent as absolutes, ten samples a second had to carry all of that, while the
         * receiver's own scene moved the same station sixty times a second -- so the
         * ship was drawn against a body that had already moved on, and shook at the
         * beat between the two rates.
         *
         * Sending the offset instead puts the fast component -- the orbit -- on the
         * receiver, who can resolve it every frame, and leaves only the slow one --
         * the ship's own drift relative to the body -- to be interpolated.
         */
        this.anchor = '';
        /**
         * Which berth, when docked.
         *
         * Also what makes a pad occupied: nobody else may set down on a pad somebody
         * is already parked on, and this is the only statement of who is where.
         */
        this.stationId = '';
        this.padId = '';
        this.joints = new ArraySchema();
        this.lines = new ArraySchema();
        this.components = new ArraySchema();
    }
}
__decorate([
    type('string')
], ShipState.prototype, "id", void 0);
__decorate([
    type('string')
], ShipState.prototype, "ownerUuid", void 0);
__decorate([
    type('string')
], ShipState.prototype, "callsign", void 0);
__decorate([
    type('boolean')
], ShipState.prototype, "isNpc", void 0);
__decorate([
    type('number')
], ShipState.prototype, "x", void 0);
__decorate([
    type('number')
], ShipState.prototype, "y", void 0);
__decorate([
    type('number')
], ShipState.prototype, "z", void 0);
__decorate([
    type('number')
], ShipState.prototype, "qx", void 0);
__decorate([
    type('number')
], ShipState.prototype, "qy", void 0);
__decorate([
    type('number')
], ShipState.prototype, "qz", void 0);
__decorate([
    type('number')
], ShipState.prototype, "qw", void 0);
__decorate([
    type('number')
], ShipState.prototype, "vx", void 0);
__decorate([
    type('number')
], ShipState.prototype, "vy", void 0);
__decorate([
    type('number')
], ShipState.prototype, "vz", void 0);
__decorate([
    type('boolean')
], ShipState.prototype, "hidden", void 0);
__decorate([
    type('boolean')
], ShipState.prototype, "docked", void 0);
__decorate([
    type('string')
], ShipState.prototype, "anchor", void 0);
__decorate([
    type('string')
], ShipState.prototype, "stationId", void 0);
__decorate([
    type('string')
], ShipState.prototype, "padId", void 0);
__decorate([
    type([JointState])
], ShipState.prototype, "joints", void 0);
__decorate([
    type([LineState])
], ShipState.prototype, "lines", void 0);
__decorate([
    type([ComponentState])
], ShipState.prototype, "components", void 0);
export class PlayerState extends Schema {
    constructor() {
        super(...arguments);
        this.sessionId = '';
        this.uuid = '';
        this.displayId = '';
        /** Empty while respawning. */
        this.shipId = '';
    }
}
__decorate([
    type('string')
], PlayerState.prototype, "sessionId", void 0);
__decorate([
    type('string')
], PlayerState.prototype, "uuid", void 0);
__decorate([
    type('string')
], PlayerState.prototype, "displayId", void 0);
__decorate([
    type('string')
], PlayerState.prototype, "shipId", void 0);
/** One room per system. roomId is the systemId. */
export class SystemState extends Schema {
    constructor() {
        super(...arguments);
        this.systemId = '';
        this.tick = 0;
        this.players = new MapSchema();
        this.ships = new MapSchema();
    }
}
__decorate([
    type('string')
], SystemState.prototype, "systemId", void 0);
__decorate([
    type('number')
], SystemState.prototype, "tick", void 0);
__decorate([
    type({ map: PlayerState })
], SystemState.prototype, "players", void 0);
__decorate([
    type({ map: ShipState })
], SystemState.prototype, "ships", void 0);
//# sourceMappingURL=ship.js.map